import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetAssessmentDietDetailsQuery,
  useGetAssessmentAssessorsPoolQuery,
  useSyncAssessmentDietAssessorsMutation,
  useRemoveAssessmentDietAssessorMutation,
  useGetAssessmentAwaitingLogbooksQuery,
  useAssignAssessmentLogbooksMutation,
  useUnassignAssessmentLogbookMutation,
  useGetAssessmentDietAssessorLogbooksQuery,
  useApplyAssessmentDietCutOffMutation,
  useGetAssessmentDietResultLetterQuery,
  useSaveAssessmentDietResultLetterMutation,
} from "../api/logbookAssessmentDietApi";
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";
import { BASE_URL } from "../../../features/api/apiSlice";
import { RecordManualResultButton } from "../components/manualResultActions";

function applicantName(lb: any): string {
  const app = lb?.application || {};
  const first = app.other_names ?? app.othernames ?? "";
  const last = app.surname ?? "";
  const name = `${last} ${first}`.trim();
  return name || lb?.user?.name || String(lb?.user_id ?? "");
}

function StatusBadge({ status }: { status?: string }) {
  const map: Record<string, string> = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    ongoing: "bg-emerald-50 text-emerald-700 border-emerald-200",
    closed: "bg-gray-100 text-gray-600 border-gray-200",
  };
  const cls = map[status || ""] || "bg-white/20 text-white border-white/30";
  return (
    <span className={`inline-flex text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${cls}`}>
      {status || "—"}
    </span>
  );
}

const PAGE_SIZE = 10;

function Pagination({
  page,
  totalItems,
  onPage,
}: {
  page: number;
  totalItems: number;
  onPage: (p: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  if (!totalItems) return null; // nothing to show only when the table is empty
  const from = (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, totalItems);
  return (
    <div className="mt-4 flex items-center justify-between gap-2 text-sm">
      <div className="text-gray-500">
        Showing <b>{from}</b>–<b>{to}</b> of <b>{totalItems}</b>
      </div>
      <div className="flex items-center gap-2">
        <button
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-40"
        >
          Prev
        </button>
        <span className="text-gray-600">
          Page {page} / {totalPages}
        </span>
        <button
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
          className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}

const KPI_TINT = [
  "from-indigo-50 to-white border-indigo-100 text-indigo-700",
  "from-sky-50 to-white border-sky-100 text-sky-700",
  "from-emerald-50 to-white border-emerald-100 text-emerald-700",
  "from-amber-50 to-white border-amber-100 text-amber-700",
  "from-purple-50 to-white border-purple-100 text-purple-700",
];

/* ── Cut-off ────────────────────────────────────────────────────────────────
   The pass mark is the Institute's decision for the whole diet, not each
   assessor's, so it is applied to everyone at once. Two different re-sits come
   out of it and the difference matters to the candidate more than anything
   else on this page: falling short of the mark is six months, contravening the
   submission rules is twelve. */

function CutOffOutcome({ label, value, tone, hint }: {
  label: string; value: number; tone: string; hint?: string;
}) {
  return (
    <div className={`rounded-xl border p-3 ${tone}`}>
      <div className="text-2xl font-extrabold leading-none">{value}</div>
      <div className="text-[11px] font-semibold uppercase tracking-wide mt-1">{label}</div>
      {hint && <div className="text-[11px] opacity-70 mt-0.5">{hint}</div>}
    </div>
  );
}

function CutOffPanel({
  dietId, diet, notify, primaryBtn,
}: {
  dietId: string;
  diet: any;
  notify: (kind: "ok" | "err", msg: string) => void;
  primaryBtn: string;
}) {
  const [cutOff, setCutOff] = useState<string>(diet?.pass_mark ? String(diet.pass_mark) : "50");
  const [preview, setPreview] = useState<any | null>(null);
  const [apply, { isLoading }] = useApplyAssessmentDietCutOffMutation();

  const mark = Number(cutOff);
  const valid = Number.isFinite(mark) && mark >= 1 && mark <= 100;

  const run = async (dryRun: boolean) => {
    if (!valid) {
      notify("err", "Enter a cut-off mark between 1 and 100.");
      return;
    }

    // The live run rewrites every candidate's verdict in this diet, so it is
    // spelled out rather than confirmed with a bare "Are you sure?".
    if (!dryRun) {
      const counts = preview
        ? `\n\n${preview.passed} pass, ${preview.failed_below_cut_off} fail (6-month re-sit), `
          + `${preview.contravened_in_diet + preview.contravened_awaiting} contravened (12-month re-sit).`
        : "";
      if (!window.confirm(
        `Apply a cut-off of ${mark} to every assessed logbook in this diet?${counts}`
        + "\n\nResults are NOT released to members by this — the board still has to approve them.",
      )) return;
    }

    try {
      const res: any = await apply({ id: dietId, cut_off: mark, dry_run: dryRun }).unwrap();
      setPreview(res?.summary ?? null);
      notify("ok", res?.message ?? (dryRun ? "Preview ready." : "Cut-off applied."));
    } catch (e: any) {
      notify("err", e?.data?.message ?? "Could not apply the cut-off.");
    }
  };

  const applied = !!diet?.pass_mark;

  return (
    <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-between gap-2 p-5 border-b bg-amber-50/50">
        <div>
          <div className="font-semibold text-gray-900">Cut-off &amp; Grading</div>
          <div className="text-sm text-gray-500">
            Grade every assessed logbook in this diet against a pass mark, and set re-sit periods.
          </div>
        </div>
        {applied && (
          <span className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-100 text-amber-800 border border-amber-200 whitespace-nowrap">
            Applied at {diet.pass_mark}
            {diet.graded_at ? ` · ${new Date(diet.graded_at).toLocaleDateString()}` : ""}
          </span>
        )}
      </div>

      <div className="p-5 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Cut-off mark (out of 100)
            </label>
            <input
              type="number"
              min={1}
              max={100}
              value={cutOff}
              onChange={(e) => { setCutOff(e.target.value); setPreview(null); }}
              className="w-32 border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <button onClick={() => run(true)} disabled={isLoading || !valid}
            className="px-4 py-2 rounded-lg border text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
            {isLoading ? "Working…" : "Preview"}
          </button>
          <button onClick={() => run(false)} disabled={isLoading || !valid} className={primaryBtn}>
            {applied ? "Re-apply cut-off" : "Apply cut-off"}
          </button>
        </div>

        {preview && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <CutOffOutcome label="Pass" value={preview.passed}
              tone="bg-emerald-50 border-emerald-200 text-emerald-800" />
            <CutOffOutcome label="Fail" value={preview.failed_below_cut_off}
              tone="bg-rose-50 border-rose-200 text-rose-800"
              hint={`Re-sit ${preview.resit_due_at_6_months}`} />
            <CutOffOutcome label="Contravened (awaiting)" value={preview.contravened_awaiting ?? 0}
              tone="bg-orange-50 border-orange-200 text-orange-800"
              hint={`Re-sit ${preview.resit_due_at_12_months}`} />
            <CutOffOutcome label="Contravened (in diet)" value={preview.contravened_in_diet}
              tone="bg-orange-50 border-orange-200 text-orange-800"
              hint={`Re-sit ${preview.resit_due_at_12_months}`} />
            <CutOffOutcome label="No member record" value={preview.no_member_record ?? 0}
              tone="bg-rose-50 border-rose-200 text-rose-800"
              hint={`Failed · re-sit ${preview.resit_due_at_12_months}`} />
            <CutOffOutcome label="Not yet assessed" value={preview.left_unassessed}
              tone="bg-gray-50 border-gray-200 text-gray-700" hint="Left untouched" />
            <CutOffOutcome label="Already decided" value={preview.already_decided ?? 0}
              tone="bg-gray-50 border-gray-200 text-gray-700" hint="Existing result kept" />
          </div>
        )}

        <p className="text-xs text-gray-500 leading-relaxed">
          Below the mark is a <b>6-month</b> re-sit. A contravening logbook is a <b>12-month</b>
          {" "}re-sit — those are stopped from assessment entirely, so most sit in the awaiting pool
          rather than in this diet, and both are covered. A candidate with <b>no member record</b> is
          failed on the same 12-month terms whatever they scored: with no membership number they
          cannot be checked against the contravention register at all. Anything merely still awaiting
          its assessor is left alone, and a logbook that <b>already carries a result is never
          overturned</b>. Nothing here is shown to members: the board's result approval is still what
          releases a result, so the mark can be previewed and changed until then.
        </p>
      </div>
    </div>
  );
}

/* ── Result letter ──────────────────────────────────────────────────────────
   The letter a candidate receives, worded per OUTCOME and per diet. It used to
   be one hardcoded block that congratulated everybody, which was harmless only
   while every diet-assessed result was a pass — the moment a cut-off produces
   re-sits, the same text would tell someone facing a twelve-month wait that
   they had succeeded.

   Editing here touches THIS diet only. A logbook with no assessment diet has no
   settings to read and keeps the wording it has always had. */

const OUTCOME_LABELS: Record<string, string> = {
  pass: "Pass",
  resit_6_months: "Re-sit · 6 months",
  resit_12_months: "Re-sit · 12 months (contravention)",
};

function ResultLetterPanel({
  dietId, notify, primaryBtn,
}: {
  dietId: string;
  notify: (kind: "ok" | "err", msg: string) => void;
  primaryBtn: string;
}) {
  const { data, isLoading } = useGetAssessmentDietResultLetterQuery(dietId, { skip: !dietId });
  const [save, { isLoading: saving }] = useSaveAssessmentDietResultLetterMutation();

  const [draft, setDraft] = useState<any | null>(null);
  const [open, setOpen] = useState(false);

  // Load the server's merged settings once, then leave the admin's edits alone
  // — refetching mid-edit would throw away what they had typed.
  useEffect(() => {
    if (data?.settings && !draft) setDraft(JSON.parse(JSON.stringify(data.settings)));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const setOutcome = (key: string, field: string, value: string) =>
    setDraft((d: any) => ({
      ...d,
      outcomes: { ...d.outcomes, [key]: { ...d.outcomes[key], [field]: value } },
    }));

  const onSave = async () => {
    try {
      const res: any = await save({ id: dietId, body: draft }).unwrap();
      setDraft(JSON.parse(JSON.stringify(res.settings)));
      notify("ok", res?.message ?? "Saved.");
    } catch (e: any) {
      notify("err", e?.data?.message ?? "Could not save the letter wording.");
    }
  };

  const onReset = async () => {
    if (!window.confirm("Discard this diet's wording and go back to the default letters?")) return;
    try {
      const res: any = await save({ id: dietId, body: { reset: true } }).unwrap();
      setDraft(JSON.parse(JSON.stringify(res.settings)));
      notify("ok", res?.message ?? "Reset to defaults.");
    } catch (e: any) {
      notify("err", e?.data?.message ?? "Could not reset the wording.");
    }
  };

  return (
    <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 p-5 border-b bg-sky-50/50 text-left"
      >
        <div>
          <div className="font-semibold text-gray-900">Result Letter</div>
          <div className="text-sm text-gray-500">
            What this diet's candidates are told — worded separately for a pass and for each re-sit.
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {data && (
            <span className={`text-xs font-semibold px-3 py-1.5 rounded-lg border whitespace-nowrap ${
              data.is_customised
                ? "bg-sky-100 text-sky-800 border-sky-200"
                : "bg-gray-100 text-gray-500 border-gray-200"
            }`}>
              {data.is_customised ? "Customised" : "Using defaults"}
            </span>
          )}
          <span className="text-gray-400 text-sm">{open ? "▲" : "▼"}</span>
        </div>
      </button>

      {open && (
        <div className="p-5 space-y-5">
          {isLoading || !draft ? (
            <div className="text-sm text-gray-500">Loading…</div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Signatory</label>
                  <input
                    value={draft.signatory_name}
                    onChange={(e) => setDraft((d: any) => ({ ...d, signatory_name: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Title</label>
                  <input
                    value={draft.signatory_title}
                    onChange={(e) => setDraft((d: any) => ({ ...d, signatory_title: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-100"
                  />
                </div>
              </div>

              {Object.keys(draft.outcomes).map((key) => (
                <div key={key} className="border rounded-xl overflow-hidden">
                  <div className="px-4 py-2.5 bg-gray-50 border-b text-xs font-black uppercase tracking-wide text-gray-600">
                    {OUTCOME_LABELS[key] ?? key}
                  </div>
                  <div className="p-4 space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Subject</label>
                      <input
                        value={draft.outcomes[key].subject ?? ""}
                        onChange={(e) => setOutcome(key, "subject", e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-100"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Body</label>
                      <textarea
                        value={draft.outcomes[key].body ?? ""}
                        onChange={(e) => setOutcome(key, "body", e.target.value)}
                        rows={6}
                        className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-100"
                      />
                    </div>
                    {key === "pass" && (
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">
                          Body for the final stage
                          <span className="font-normal text-gray-400">
                            {" "}— used instead of the above when there is no next stage to send them to
                          </span>
                        </label>
                        <textarea
                          value={draft.outcomes[key].final_stage_body ?? ""}
                          onChange={(e) => setOutcome(key, "final_stage_body", e.target.value)}
                          rows={5}
                          className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-100"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div className="text-xs text-gray-500 leading-relaxed">
                <span className="font-semibold">Placeholders:</span>{" "}
                {Object.keys(data?.placeholders ?? {}).map((p) => (
                  <code key={p} className="bg-gray-100 rounded px-1.5 py-0.5 mr-1.5 text-gray-700">{p}</code>
                ))}
              </div>

              <div className="flex flex-wrap gap-2">
                <button onClick={onSave} disabled={saving} className={primaryBtn}>
                  {saving ? "Saving…" : "Save wording"}
                </button>
                <button
                  onClick={onReset}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  Reset to defaults
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminAssessmentDietDetailsPage() {
  const navigate = useNavigate();
  const { id = "" } = useParams();

  const { data, error, isLoading, isError, refetch } = useGetAssessmentDietDetailsQuery(id, { skip: !id });

  const diet = data?.diet;
  const summary = data?.summary;
  const assessors = data?.assessors || [];
  const logbooks = data?.logbooks || [];

  const [banner, setBanner] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);
  const notify = (kind: "ok" | "err", msg: string) => setBanner({ kind, msg });

  /* ---- Section 1: assessors ------------------------------------------- */
  const [manageOpen, setManageOpen] = useState(false);
  const { data: pool = [], isFetching: loadingPool } = useGetAssessmentAssessorsPoolQuery(undefined, {
    skip: !manageOpen,
  });
  const [selectedAssessorIds, setSelectedAssessorIds] = useState<string[]>([]);
  const [syncAssessors, { isLoading: syncing }] = useSyncAssessmentDietAssessorsMutation();
  const [removeAssessor] = useRemoveAssessmentDietAssessorMutation();

  // Preselect the diet's current assessors when the manage modal opens.
  useEffect(() => {
    if (manageOpen) {
      setSelectedAssessorIds(assessors.map((a: any) => String(a.member_id)));
    }
  }, [manageOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleAssessor = (memberId: string) =>
    setSelectedAssessorIds((prev) =>
      prev.includes(memberId) ? prev.filter((x) => x !== memberId) : [...prev, memberId],
    );

  const saveAssessors = async () => {
    try {
      await syncAssessors({ id, assessor_member_ids: selectedAssessorIds }).unwrap();
      notify("ok", "Assessors updated.");
      setManageOpen(false);
      refetch();
    } catch (e: any) {
      notify("err", String(e?.data?.message || e?.message || "Failed to update assessors."));
    }
  };

  const removeOne = async (memberId: string, name: string) => {
    if (!window.confirm(`Remove ${name} from this assessment diet?`)) return;
    try {
      await removeAssessor({ id, memberId }).unwrap();
      notify("ok", "Assessor removed.");
      refetch();
    } catch (e: any) {
      notify("err", String(e?.data?.message || e?.message || "Failed to remove assessor."));
    }
  };

  /* ---- Section 2: logbook assignment ---------------------------------- */
  const [assignOpen, setAssignOpen] = useState(false);
  const [pickedAssessor, setPickedAssessor] = useState("");
  const [pickedLogbooks, setPickedLogbooks] = useState<string[]>([]);
  const {
    data: awaiting = [],
    isFetching: loadingAwaiting,
    refetch: refetchAwaiting,
  } = useGetAssessmentAwaitingLogbooksQuery(); // fetched always — also powers the "Awaiting Assessment" KPI
  const [assignLogbooks, { isLoading: assigning }] = useAssignAssessmentLogbooksMutation();
  const [unassignLogbook] = useUnassignAssessmentLogbookMutation();

  /* ---- View one assessor's assigned logbooks -------------------------- */
  const [viewAssessor, setViewAssessor] = useState<{ memberId: string; name: string } | null>(null);
  const {
    data: assessorLogbooksRes,
    isFetching: loadingAssessorLogbooks,
    refetch: refetchAssessorLogbooks,
  } = useGetAssessmentDietAssessorLogbooksQuery(
    { id, memberId: viewAssessor?.memberId || "" },
    { skip: !viewAssessor },
  );
  const assessorLogbooks: any[] = assessorLogbooksRes?.data ?? [];

  // Remove an UNASSESSED logbook from its assessor → back to the awaiting pool.
  const doUnassignFromAssessor = async (logbookId: string) => {
    if (!window.confirm("Remove this logbook from the assessor and return it to the awaiting pool?")) return;
    try {
      await unassignLogbook({ id, logbookId }).unwrap();
      notify("ok", "Logbook returned to the awaiting pool.");
      refetch();
      refetchAssessorLogbooks();
      refetchAwaiting();
    } catch (e: any) {
      notify("err", String(e?.data?.message || e?.message || "Failed to unassign logbook."));
    }
  };

  /* ---- Pagination (client-side) --------------------------------------- */
  const [logbookPage, setLogbookPage] = useState(1);
  const [assessorLogbookPage, setAssessorLogbookPage] = useState(1);

  // Keep pages in range when the underlying data changes.
  useEffect(() => {
    setLogbookPage((p) => Math.min(Math.max(1, p), Math.max(1, Math.ceil(logbooks.length / PAGE_SIZE))));
  }, [logbooks.length]);
  useEffect(() => {
    setAssessorLogbookPage(1);
  }, [viewAssessor?.memberId, assessorLogbooks.length]);

  const pagedLogbooks = logbooks.slice((logbookPage - 1) * PAGE_SIZE, logbookPage * PAGE_SIZE);
  const pagedAssessorLogbooks = assessorLogbooks.slice(
    (assessorLogbookPage - 1) * PAGE_SIZE,
    assessorLogbookPage * PAGE_SIZE,
  );

  const openAssign = () => {
    setPickedAssessor("");
    setPickedLogbooks([]);
    setAssignOpen(true);
  };

  const toggleLogbook = (logbookId: string) =>
    setPickedLogbooks((prev) =>
      prev.includes(logbookId) ? prev.filter((x) => x !== logbookId) : [...prev, logbookId],
    );

  const doAssign = async () => {
    if (!pickedAssessor) return notify("err", "Select an assessor.");
    if (!pickedLogbooks.length) return notify("err", "Select at least one logbook.");
    try {
      const res: any = await assignLogbooks({
        id,
        assessor_member_id: pickedAssessor,
        logbook_ids: pickedLogbooks,
      }).unwrap();
      notify("ok", res?.message || "Logbooks assigned.");
      setPickedLogbooks([]);
      setAssignOpen(false);
      refetch();
    } catch (e: any) {
      notify("err", String(e?.data?.message || e?.message || "Failed to assign logbooks."));
    }
  };

  const doUnassign = async (logbookId: string) => {
    if (!window.confirm("Remove this logbook from the assessment diet?")) return;
    try {
      await unassignLogbook({ id, logbookId }).unwrap();
      notify("ok", "Logbook unassigned.");
      refetch();
    } catch (e: any) {
      notify("err", String(e?.data?.message || e?.message || "Failed to unassign logbook."));
    }
  };

  /* ---- Result sheet (PDF / Excel) ------------------------------------- */
  // Plain fetch rather than RTK Query: the response is a file attachment, not
  // JSON, and it must never be cached in the store.
  const [downloading, setDownloading] = useState<"pdf" | "csv" | null>(null);
  const [stageFilter, setStageFilter] = useState(""); // "" = every stage
  const [approvedOnly, setApprovedOnly] = useState(false);

  // How many assessed logbooks the current filter would actually report on —
  // so the buttons can refuse to generate an empty sheet.
  const reportableCount = useMemo(
    () =>
      logbooks.filter(
        (lb: any) =>
          lb?.graded_at &&
          (!stageFilter || String(lb?.stage) === stageFilter) &&
          (!approvedOnly || lb?.result_approved_at),
      ).length,
    [logbooks, stageFilter, approvedOnly],
  );

  const downloadResultSheet = async (format: "pdf" | "csv") => {
    setDownloading(format);
    try {
      const params = new URLSearchParams();
      if (stageFilter) params.set("stage", stageFilter);
      if (approvedOnly) params.set("approved_only", "1");
      const qs = params.toString();

      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/logbook-assessment-diets/${id}/results/${format}${qs ? `?${qs}` : ""}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );

      if (!res.ok) throw new Error(`Server returned ${res.status}`);

      // This API emits stray output ahead of its response body (which also
      // flushes the headers, so Content-Type cannot be trusted) — the file then
      // arrives with a junk prefix. Locate where the real content starts rather
      // than demanding byte 0; only a body with no file in it is a real error.
      const bytes = new Uint8Array(await res.arrayBuffer());
      const probe = new TextDecoder("latin1").decode(bytes.subarray(0, 4096));

      let start: number;
      if (format === "pdf") {
        start = probe.indexOf("%PDF-");
        if (start === -1) {
          throw new Error(
            new TextDecoder().decode(bytes.subarray(0, 300)).trim() ||
              "The server did not return a PDF.",
          );
        }
      } else {
        // CSV has no magic number. A JSON error body is the thing to catch;
        // otherwise skip any leading whitespace the API prepended.
        if (probe.trimStart().startsWith("{")) {
          throw new Error(
            new TextDecoder().decode(bytes.subarray(0, 300)).trim() ||
              "The server did not return a spreadsheet.",
          );
        }
        // First non-whitespace byte. `probe` is latin1, so the UTF-8 BOM reads
        // as "ï»¿" — non-whitespace — and is therefore preserved, which Excel
        // needs to read the file as UTF-8.
        start = probe.search(/\S/);
        if (start < 0) start = 0;
      }

      if (start > 0) {
        // Not fatal, but it means the API is leaking output before its
        // responses, which corrupts every file download it serves.
        console.warn(`[result sheet] discarded ${start} stray byte(s) before the ${format}`);
      }

      // Typed here so the browser saves it correctly whatever the server said.
      const blob = new Blob([bytes.subarray(start)], {
        type: format === "pdf" ? "application/pdf" : "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = [
        (diet?.title || "assessment-diet").replace(/[^\w-]+/g, "-").toLowerCase(),
        stageFilter ? `stage-${stageFilter}` : null,
        approvedOnly ? "approved" : null,
        "results",
      ]
        .filter(Boolean)
        .join("-") + `.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      notify("ok", `Result sheet generated (${reportableCount} candidate(s)).`);
    } catch (e: any) {
      notify("err", String(e?.message || "Failed to generate the result sheet."));
    } finally {
      setDownloading(null);
    }
  };

  const assessorName = useMemo(() => {
    const m = new Map<string, string>();
    assessors.forEach((a: any) => m.set(String(a.member_id), a.name));
    return (memberId: string | null) => (memberId ? m.get(String(memberId)) || "—" : "Unassigned");
  }, [assessors]);

  if (isLoading) return <div className="text-gray-600 p-6">Loading assessment diet…</div>;

  if (isError || !data || !diet) {
    // Surface what the server actually said. The API currently returns HTTP 200
    // for errors (stray output flushes the headers before Laravel can set the
    // status), so a failure arrives as a "successful" body with no `diet` — the
    // real reason is in data.message. Without this the page can only say
    // "failed", which hides parse errors, SQL errors and expired sessions alike.
    const err: any = error;
    const rawBody = typeof err?.data === "string" ? err.data : null;
    // `||` not `??` — an empty-string body is falsy but not nullish, and an
    // empty body is itself the diagnosis (a PHP fatal that printed nothing).
    const serverMessage =
      (data as any)?.message ||
      err?.data?.message ||
      (rawBody ? rawBody.slice(0, 300) : null) ||
      (rawBody === "" ? "(empty response body)" : null) ||
      err?.error ||
      null;
    // On PARSING_ERROR the true HTTP status is in originalStatus, not status.
    const status = [err?.status, err?.originalStatus ? `HTTP ${err.originalStatus}` : null]
      .filter(Boolean)
      .join(" · ") || (data && !diet ? "200 (error body)" : null);
    const unauthenticated = /unauthenticated/i.test(String(serverMessage ?? ""));

    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="rounded-xl border border-red-200 bg-red-50 p-5">
          <h2 className="font-bold text-red-800">Failed to load assessment diet.</h2>

          {serverMessage ? (
            <p className="mt-2 text-sm text-red-700">
              <span className="font-semibold">Server said:</span>{" "}
              <span className="font-mono break-words">{String(serverMessage)}</span>
            </p>
          ) : (
            <p className="mt-2 text-sm text-red-700">
              The server returned no usable response. Check the network connection.
            </p>
          )}

          {status ? (
            <p className="mt-1 text-xs text-red-600/80">Status: {String(status)}</p>
          ) : null}

          {rawBody === "" ? (
            <p className="mt-2 text-sm text-red-700">
              The server closed the response without sending anything — usually a PHP fatal
              (memory or execution-time limit). The reason will be in the API's{" "}
              <span className="font-mono">storage/logs/laravel.log</span>.
            </p>
          ) : null}

          {unauthenticated ? (
            <p className="mt-2 text-sm text-red-700">Your session has expired — sign in again.</p>
          ) : null}

          <div className="mt-4 flex gap-2">
            <button
              onClick={() => refetch()}
              className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700"
            >
              Retry
            </button>
            <button
              onClick={() => navigate("/admin/logbook-assessment")}
              className="px-3 py-1.5 rounded-lg border border-red-300 text-red-700 text-sm font-medium hover:bg-red-100"
            >
              Back to diets
            </button>
          </div>
        </div>
      </div>
    );
  }

  const primaryBtn =
    "px-4 py-2 rounded-lg bg-gradient-purple text-white text-sm font-medium hover:opacity-90 transition disabled:opacity-50";

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5">
      {/* Header banner */}
      <div className="rounded-2xl bg-gradient-purple text-white p-6 shadow-sm">
        <button
          onClick={() => navigate("/admin/logbook-assessment")}
          className="text-sm text-white/80 hover:text-white"
        >
          ← Back to Assessment Diets
        </button>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold">{diet.title}</h1>
              <StatusBadge status={diet.status} />
            </div>
            <p className="text-sm text-white/80 mt-1">
              {diet.start_date || "—"} → {diet.end_date || "—"}
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="self-start sm:self-auto px-4 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-white text-sm"
          >
            Refresh
          </button>
        </div>

        {/* Result report — filter, then download */}
        <div className="mt-4 pt-4 border-t border-white/20 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] uppercase tracking-wide text-white/70 mb-1">
              Stage
            </label>
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              className="px-3 py-2 rounded-lg text-sm text-gray-900 bg-white border-0"
            >
              <option value="">All stages</option>
              <option value="1">Stage 1</option>
              <option value="2">Stage 2</option>
              <option value="3">Stage 3</option>
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm text-white/90 pb-2">
            <input
              type="checkbox"
              checked={approvedOnly}
              onChange={(e) => setApprovedOnly(e.target.checked)}
              className="w-4 h-4 rounded"
            />
            Approved results only
          </label>

          <div className="flex flex-wrap items-center gap-2 pb-0.5">
            <button
              onClick={() => downloadResultSheet("pdf")}
              disabled={!!downloading || !reportableCount}
              title={
                reportableCount
                  ? "Download the result sheet as a PDF"
                  : "No assessed logbook matches this filter"
              }
              className="px-4 py-2 rounded-lg bg-white text-purple-700 text-sm font-semibold hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {downloading === "pdf" ? "Generating…" : "Download PDF"}
            </button>
            <button
              onClick={() => downloadResultSheet("csv")}
              disabled={!!downloading || !reportableCount}
              title={
                reportableCount
                  ? "Download the same sheet as a spreadsheet (opens in Excel)"
                  : "No assessed logbook matches this filter"
              }
              className="px-4 py-2 rounded-lg bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {downloading === "csv" ? "Generating…" : "Download Excel"}
            </button>
            <span className="text-xs text-white/70">
              {reportableCount} candidate{reportableCount === 1 ? "" : "s"} in this report
            </span>
          </div>
        </div>
      </div>

      {banner ? (
        <div
          className={`text-sm rounded-lg p-3 border ${
            banner.kind === "ok"
              ? "text-emerald-700 bg-emerald-50 border-emerald-100"
              : "text-red-700 bg-red-50 border-red-100"
          }`}
        >
          {banner.msg}
        </div>
      ) : null}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {[
          { label: "Total Assigned", value: summary?.total_logbooks ?? 0 },
          { label: "Awaiting Assessment", value: loadingAwaiting ? "…" : awaiting.length },
          { label: "Assessed", value: summary?.assessed ?? 0 },
          { label: "Pending", value: summary?.pending ?? 0 },
          { label: "Assessors", value: summary?.total_assessors ?? 0 },
        ].map((k, i) => (
          <div key={k.label} className={`rounded-xl border bg-gradient-to-b p-4 ${KPI_TINT[i]}`}>
            <div className="text-xs font-semibold uppercase tracking-wide opacity-80">{k.label}</div>
            <div className="text-3xl font-extrabold mt-1 text-gray-900">{k.value}</div>
          </div>
        ))}
      </div>

      {/* Cut-off — only while the diet's results are still unpublished. Once a
          result is out it belongs to the candidate, and the API refuses to
          re-grade it. */}
      <AccessGate perm={PERMS.logbook_diet.assign}>
        <CutOffPanel dietId={id} diet={diet} notify={notify} primaryBtn={primaryBtn} />
      </AccessGate>

      {/* What this diet's candidates are actually told. */}
      <AccessGate perm={PERMS.logbook_diet.assign}>
        <ResultLetterPanel dietId={id} notify={notify} primaryBtn={primaryBtn} />
      </AccessGate>

      {/* Section 1 — Assessors */}
      <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between gap-2 p-5 border-b bg-indigo-50/40">
          <div>
            <div className="font-semibold text-gray-900">Assessors</div>
            <div className="text-sm text-gray-500">Attach the assessors who will grade this cycle.</div>
          </div>
          <AccessGate perm={PERMS.logbook_diet.assign}>
            <button onClick={() => setManageOpen(true)} className={primaryBtn}>
              Manage Assessors
            </button>
          </AccessGate>
        </div>

        <div className="p-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 font-semibold">Name</th>
                <th className="py-2 font-semibold">Assigned</th>
                <th className="py-2 font-semibold">Assessed</th>
                <th className="py-2 font-semibold">Pending</th>
                <th className="py-2 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {assessors.map((a: any) => (
                <tr key={a.member_id} className="border-b last:border-0 hover:bg-gray-50/60">
                  <td className="py-3 font-semibold text-gray-800">
                    {a.assessor_id ? (
                      <button
                        onClick={() => navigate(`/assessors/${a.assessor_id}`)}
                        className="text-indigo-700 hover:text-indigo-900 hover:underline"
                      >
                        {a.name}
                      </button>
                    ) : (
                      a.name
                    )}
                  </td>
                  <td className="py-3">{a.total_assigned_logbooks}</td>
                  <td className="py-3 text-emerald-700 font-medium">{a.total_assessed_logbooks}</td>
                  <td className="py-3 text-amber-700 font-medium">{a.total_pending_logbooks}</td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setViewAssessor({ memberId: a.member_id, name: a.name })}
                        className="px-3 py-1.5 rounded-lg border border-purple-200 text-purple-700 hover:bg-purple-50 text-xs"
                      >
                        View logbooks
                      </button>
                      {a.assessor_id ? (
                        <button
                          onClick={() => navigate(`/assessors/${a.assessor_id}`)}
                          className="px-3 py-1.5 rounded-lg border border-indigo-200 text-indigo-700 hover:bg-indigo-50 text-xs"
                        >
                          View profile
                        </button>
                      ) : null}
                      <AccessGate perm={PERMS.logbook_diet.assign}>
                        <button
                          onClick={() => removeOne(a.member_id, a.name)}
                          className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-xs"
                        >
                          Remove
                        </button>
                      </AccessGate>
                    </div>
                  </td>
                </tr>
              ))}
              {!assessors.length ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-500">
                    No assessors attached yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 2 — Logbook assignment */}
      <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between gap-2 p-5 border-b bg-purple-50/40">
          <div>
            <div className="font-semibold text-gray-900">Assigned Logbooks</div>
            <div className="text-sm text-gray-500">
              Assign logbooks awaiting assessment to this diet's assessors.
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <AccessGate perm={PERMS.logbook_diet.assign}>
              <RecordManualResultButton dietId={id} dietTitle={diet?.title} />
            </AccessGate>
            <AccessGate perm={PERMS.logbook_diet.assign}>
              <button
                onClick={openAssign}
                disabled={!assessors.length}
                title={!assessors.length ? "Attach an assessor first" : ""}
                className={primaryBtn}
              >
                Assign Awaiting Logbooks
              </button>
            </AccessGate>
          </div>
        </div>

        <div className="p-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 font-semibold">Candidate</th>
                <th className="py-2 font-semibold">Stage</th>
                <th className="py-2 font-semibold">Assessor</th>
                <th className="py-2 font-semibold">Status</th>
                <th className="py-2 font-semibold">Score</th>
                <th className="py-2 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {pagedLogbooks.map((lb: any) => {
                const graded = lb.status === "graded" || lb.graded_at;
                return (
                  <tr key={lb.id} className="border-b last:border-0 hover:bg-gray-50/60">
                    <td className="py-3 font-semibold text-gray-800">
                      {applicantName(lb)}
                      {lb.manual_result ? (
                        <span
                          title="Typed in by an admin from a paper/off-system record"
                          className="ml-2 inline-flex text-[10px] font-bold px-1.5 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200 align-middle"
                        >
                          Manual
                        </span>
                      ) : null}
                    </td>
                    <td className="py-3">{lb.stage}</td>
                    <td className="py-3">{assessorName(lb.assessor_id)}</td>
                    <td className="py-3">
                      <span
                        className={`inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full border ${
                          graded
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-amber-50 text-amber-700 border-amber-200"
                        }`}
                      >
                        {lb.status}
                      </span>
                    </td>
                    <td className="py-3 font-medium">{lb.total_score ?? "—"}</td>
                    <td className="py-3 text-right">
                      {!graded ? (
                        <AccessGate perm={PERMS.logbook_diet.assign}>
                          <button
                            onClick={() => doUnassign(lb.id)}
                            className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs"
                          >
                            Unassign
                          </button>
                        </AccessGate>
                      ) : (
                        <span className="text-xs text-gray-400">Assessed</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!logbooks.length ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">
                    No logbooks assigned to this diet yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          <Pagination page={logbookPage} totalItems={logbooks.length} onPage={setLogbookPage} />
        </div>
      </div>

      {/* Manage assessors modal */}
      {manageOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white border shadow-xl p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Manage Assessors</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Select the assessors for this cycle. Unchecking removes them.
                </p>
              </div>
              <button onClick={() => setManageOpen(false)} className="px-2 py-1 rounded border hover:bg-gray-50">
                ✕
              </button>
            </div>

            <div className="mt-4 max-h-[360px] overflow-y-auto pr-1 space-y-2">
              {loadingPool ? (
                <div className="text-sm text-gray-500">Loading assessors…</div>
              ) : (
                pool.map((a) => {
                  const checked = selectedAssessorIds.includes(String(a.member_id));
                  return (
                    <label
                      key={a.member_id}
                      className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer hover:bg-indigo-50/40 ${
                        checked ? "bg-indigo-50 border-indigo-200" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAssessor(String(a.member_id))}
                        className="h-4 w-4 accent-indigo-600"
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-800 truncate">{a.name}</div>
                        {a.email ? <div className="text-xs text-gray-500 truncate">{a.email}</div> : null}
                      </div>
                    </label>
                  );
                })
              )}
              {!loadingPool && !pool.length ? (
                <div className="text-sm text-gray-500">No assessors available in the pool.</div>
              ) : null}
            </div>

            <div className="mt-5 flex items-center justify-between gap-2">
              <div className="text-xs text-gray-600">
                Selected: <b>{selectedAssessorIds.length}</b>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setManageOpen(false)}
                  className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
                  disabled={syncing}
                >
                  Cancel
                </button>
                <button onClick={saveAssessors} disabled={syncing} className={primaryBtn}>
                  {syncing ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Assign logbooks modal */}
      {assignOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white border shadow-xl p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Assign Awaiting Logbooks</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Pick an assessor on this diet, then choose logbooks awaiting assessment.
                </p>
              </div>
              <button onClick={() => setAssignOpen(false)} className="px-2 py-1 rounded border hover:bg-gray-50">
                ✕
              </button>
            </div>

            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* LEFT: assessors on this diet */}
              <div className="border rounded-xl p-4">
                <div className="font-semibold text-gray-800">Diet Assessors</div>
                <div className="text-xs text-gray-500 mt-1">Who will assess the chosen logbooks.</div>
                <div className="mt-3 space-y-2 max-h-[340px] overflow-y-auto pr-1">
                  {assessors.map((a: any) => {
                    const memberId = String(a.member_id);
                    const active = pickedAssessor === memberId;
                    return (
                      <button
                        type="button"
                        key={memberId}
                        onClick={() => setPickedAssessor(memberId)}
                        className={`w-full text-left rounded-lg border px-3 py-2 hover:bg-indigo-50/40 ${
                          active ? "bg-indigo-50 border-indigo-300 ring-1 ring-indigo-200" : ""
                        }`}
                      >
                        <div className="text-sm font-semibold text-gray-800">{a.name}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          Assigned: {a.total_assigned_logbooks} • Pending: {a.total_pending_logbooks}
                        </div>
                      </button>
                    );
                  })}
                  {!assessors.length ? (
                    <div className="text-sm text-gray-500">No assessors attached to this diet yet.</div>
                  ) : null}
                </div>
              </div>

              {/* RIGHT: awaiting logbooks */}
              <div className="border rounded-xl p-4">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="font-semibold text-gray-800">Awaiting Assessment</div>
                    <div className="text-xs text-gray-500 mt-1">Select logbooks to assign.</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => refetchAwaiting()}
                    className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs"
                  >
                    Refresh
                  </button>
                </div>
                <div className="mt-3 max-h-[340px] overflow-y-auto pr-1 space-y-2">
                  {loadingAwaiting ? (
                    <div className="text-sm text-gray-500">Loading…</div>
                  ) : (
                    awaiting.map((lb: any) => {
                      const checked = pickedLogbooks.includes(lb.id);

                      // Contravened logbooks cannot be assigned — show a penalty
                      // action instead of a selectable checkbox.
                      if (lb.contravened) {
                        return (
                          <div
                            key={lb.id}
                            className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-2"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold text-gray-800 truncate">
                                {applicantName(lb)}
                              </div>
                              <div className="text-xs text-gray-500">
                                Stage {lb.stage} • {lb.diet?.title ?? "—"}
                              </div>
                              <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase text-rose-700 bg-rose-100 border border-rose-200 rounded-full px-2 py-0.5">
                                Contravened — not assignable
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                notify(
                                  "err",
                                  "This logbook contravened the rules. Penalty handling will be set up later."
                                )
                              }
                              className="shrink-0 px-3 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700"
                            >
                              Penalty
                            </button>
                          </div>
                        );
                      }

                      return (
                        <label
                          key={lb.id}
                          className={`flex items-start gap-3 rounded-lg border px-3 py-2 cursor-pointer hover:bg-indigo-50/40 ${
                            checked ? "bg-indigo-50 border-indigo-200" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleLogbook(lb.id)}
                            className="h-4 w-4 mt-1 accent-indigo-600"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-gray-800 truncate">
                              {applicantName(lb)}
                            </div>
                            <div className="text-xs text-gray-500">
                              Stage {lb.stage} • {lb.diet?.title ?? "—"}
                            </div>
                          </div>
                        </label>
                      );
                    })
                  )}
                  {!loadingAwaiting && !awaiting.length ? (
                    <div className="text-sm text-gray-500">No logbooks awaiting assessment.</div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between gap-2">
              <div className="text-xs text-gray-600">
                Selected logbooks: <b>{pickedLogbooks.length}</b>
                {pickedAssessor ? <> • Assessor selected ✅</> : <> • Select an assessor</>}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPickedLogbooks([]);
                    setPickedAssessor("");
                  }}
                  className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
                  disabled={assigning}
                >
                  Clear
                </button>
                <button
                  type="button"
                  disabled={!pickedAssessor || !pickedLogbooks.length || assigning}
                  onClick={doAssign}
                  className={primaryBtn}
                >
                  {assigning ? "Assigning…" : "Assign"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* View one assessor's assigned logbooks */}
      {viewAssessor ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white border shadow-xl p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Assigned Logbooks</h2>
                <p className="text-sm text-gray-500 mt-1">{viewAssessor.name}</p>
              </div>
              <button
                onClick={() => setViewAssessor(null)}
                className="px-2 py-1 rounded border hover:bg-gray-50"
              >
                ✕
              </button>
            </div>

            <div className="mt-4 max-h-[440px] overflow-auto">
              {loadingAssessorLogbooks ? (
                <div className="text-sm text-gray-500 py-6">Loading…</div>
              ) : assessorLogbooks.length ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 font-semibold">Candidate</th>
                      <th className="py-2 font-semibold">Stage</th>
                      <th className="py-2 font-semibold">Status</th>
                      <th className="py-2 font-semibold">Score</th>
                      <th className="py-2 font-semibold text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedAssessorLogbooks.map((lb: any) => {
                      const graded = lb.status === "graded" || lb.graded_at;
                      return (
                        <tr key={lb.id} className="border-b last:border-0 hover:bg-gray-50/60">
                          <td className="py-3 font-semibold text-gray-800">{applicantName(lb)}</td>
                          <td className="py-3">{lb.stage}</td>
                          <td className="py-3">
                            <span
                              className={`inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full border ${
                                graded
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "bg-amber-50 text-amber-700 border-amber-200"
                              }`}
                            >
                              {lb.status}
                            </span>
                          </td>
                          <td className="py-3 font-medium">{lb.total_score ?? "—"}</td>
                          <td className="py-3 text-right">
                            {!graded ? (
                              <AccessGate perm={PERMS.logbook_diet.assign}>
                                <button
                                  onClick={() => doUnassignFromAssessor(lb.id)}
                                  className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-xs"
                                >
                                  Unassign
                                </button>
                              </AccessGate>
                            ) : (
                              <span className="text-xs text-gray-400">Assessed</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="text-sm text-gray-500 py-6">
                  No logbooks assigned to this assessor in this diet.
                </div>
              )}
            </div>

            <Pagination
              page={assessorLogbookPage}
              totalItems={assessorLogbooks.length}
              onPage={setAssessorLogbookPage}
            />

            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setViewAssessor(null)}
                className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
